import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { MapPin, Smartphone } from 'lucide-react';

// Detect if running in PWA standalone mode
const isStandalonePWA = () => {
  return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
};

const AuthCallback = () => {
  const navigate = useNavigate();
  const { processGoogleSession } = useAuth();
  const hasProcessed = useRef(false);
  const [showPwaReturn, setShowPwaReturn] = useState(false);

  useEffect(() => {
    // Prevent double processing in StrictMode
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processSession = async () => {
      const hash = window.location.hash;
      const sessionIdMatch = hash.match(/session_id=([^&]+)/);
      
      if (sessionIdMatch) {
        const sessionId = sessionIdMatch[1];
        const result = await processGoogleSession(sessionId);
        
        if (result.success) {
          // Clear hash
          window.history.replaceState(null, '', window.location.pathname);
          
          // If we're in a regular browser but the PWA is installed,
          // the user may have been redirected here from the PWA's OAuth flow.
          // Check if NOT in standalone mode (means we're in the browser after PWA OAuth redirect)
          if (!isStandalonePWA()) {
            // Show "return to PWA" message - the PWA will pick up auth from localStorage
            setShowPwaReturn(true);
            return;
          }
          
          navigate('/dashboard', { replace: true, state: { user: result.user } });
        } else {
          navigate('/login', { replace: true });
        }
      } else {
        navigate('/login', { replace: true });
      }
    };

    processSession();
  }, [navigate, processGoogleSession]);

  // If showing PWA return message
  if (showPwaReturn) {
    return (
      <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center p-4">
        <div className="text-center max-w-md">
          <div className="w-16 h-16 bg-[#34C759] rounded-xl flex items-center justify-center mx-auto mb-4">
            <MapPin className="w-8 h-8 text-white" />
          </div>
          <h2 className="text-xl font-bold text-[#09090B] mb-2">Autenticazione completata!</h2>
          <p className="text-[#71717A] mb-6">
            Torna all'app Presenze Operai per continuare.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-[#71717A] bg-white rounded-lg p-4 border border-[#E4E4E7]">
            <Smartphone className="w-5 h-5 text-[#0055FF]" />
            <span>Apri l'app dalla schermata iniziale del tuo dispositivo</span>
          </div>
          <button
            onClick={() => navigate('/dashboard', { replace: true })}
            className="mt-4 text-[#0055FF] underline text-sm"
          >
            Oppure continua nel browser
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F4F5] flex items-center justify-center">
      <div className="text-center">
        <div className="w-16 h-16 bg-[#0055FF] rounded-xl flex items-center justify-center mx-auto mb-4 animate-pulse">
          <MapPin className="w-8 h-8 text-white" />
        </div>
        <p className="text-[#71717A]">Autenticazione in corso...</p>
      </div>
    </div>
  );
};

export default AuthCallback;
